"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProgressTracker = void 0;
var React = require("react");
var Collection_1 = require("../../_Core/Collection");
var keyboard = require("../../_Core/Keyboard");
var MathCore_1 = require("../../_Core/MathCore");
var Localization_1 = require("../../Localization");
var ProgressTrackerStyleValues_1 = require("./components/ProgressTrackerStyleValues");
var ScrollNavButton_1 = require("./components/ScrollNavButton");
var Step_1 = require("./components/Step");
var ProgressTracker_styles_1 = require("./ProgressTracker.styles");
var ProgressTracker = function (_a) {
    var CurrentStep = _a.CurrentStep, Steps = _a.Steps, OnClickStep = _a.OnClickStep;
    var _b = React.useState(true), showScroll = _b[0], setShowScroll = _b[1];
    var _c = React.useState(0), margin = _c[0], setMargin = _c[1];
    var _d = React.useState(false), nextButtonDisabled = _d[0], setNextButtonDisabled = _d[1];
    var _e = React.useState(CurrentStep === 0), prevButtonDisabled = _e[0], setPrevButtonDisabled = _e[1];
    var _f = React.useState(CurrentStep), displayStepIndex = _f[0], setDisplayStepIndex = _f[1];
    var sliderContainerRef = React.createRef();
    var sliderRef = React.createRef();
    var stepContainerRef = React.createRef();
    var progressBarRef = React.createRef();
    var internalDomElements = null;
    var getDomElements = function () {
        internalDomElements = internalDomElements || {
            ProgressBar: progressBarRef.current,
            Slider: sliderRef.current,
            SliderContainer: sliderContainerRef.current,
            StepContainer: stepContainerRef.current,
        };
        return internalDomElements;
    };
    React.useEffect(function () {
        var resizeHandler = function () {
            var domElements = getDomElements();
            var totalStepsWidth = sumWidths(Array.from(domElements.StepContainer.children));
            var innerContainerWidth = domElements.SliderContainer.offsetWidth;
            var show = totalStepsWidth > innerContainerWidth;
            var marginSize = ProgressTrackerStyleValues_1.StepLabelContainerWidth + 2 * ProgressTrackerStyleValues_1.ScrollNavButtonWidth;
            var currentIndex = getUnfinishedStepIndex();
            domElements.Slider.style.width = totalStepsWidth + "px";
            setDisplayStepIndex(currentIndex);
            setShowScroll(show);
            setMargin((totalStepsWidth + marginSize) > innerContainerWidth ? marginSize : 0);
            setPrevButtonDisabled(currentIndex === 0);
            setNextButtonDisabled(currentIndex === getLastDisplayIndex());
            slideToStep(currentIndex);
        };
        window.addEventListener("resize", resizeHandler);
        resizeHandler();
        return function () {
            window.removeEventListener("resize", resizeHandler);
        };
    }, []);
    React.useEffect(function () {
        setPrevButtonDisabled(displayStepIndex === 0);
        setNextButtonDisabled(displayStepIndex === getLastDisplayIndex());
        slideToStep(displayStepIndex);
        updateProgressBar();
    }, [displayStepIndex]);
    React.useEffect(function () {
        setDisplayStepIndexLimit(CurrentStep);
        slideToStep(CurrentStep);
        updateProgressBar();
    }, [CurrentStep, Steps]);
    var getUnfinishedStepIndex = function () {
        return Steps.findIndex(function (s) { return s.State !== "Complete"; });
    };
    var sumWidths = function (elements) {
        return Collection_1.Sum(elements, function (e) { return e.offsetWidth; });
    };
    var getLastDisplayIndex = function () {
        var domElements = getDomElements();
        if (showScroll) {
            var offsetSliderPosition = 0;
            var sliderWidth = sumWidths(Array.from(domElements.StepContainer.children));
            var max = sliderWidth - (domElements.SliderContainer.offsetWidth - ProgressTrackerStyleValues_1.ScrollNavButtonWidth);
            for (var i = 0; i < Steps.length; i++) {
                if (offsetSliderPosition >= max) {
                    return i;
                }
                offsetSliderPosition += domElements.StepContainer.children[i].offsetWidth;
            }
        }
        return 0;
    };
    var slideToStep = function (stepIndex) {
        var domElements = getDomElements();
        var offsetSliderPosition = 0;
        if (showScroll) {
            for (var i = 0; i < stepIndex; i++) {
                offsetSliderPosition += domElements.StepContainer.children[i].offsetWidth;
            }
            var max = domElements.Slider.offsetWidth - (domElements.SliderContainer.offsetWidth - ProgressTrackerStyleValues_1.ScrollNavButtonWidth);
            offsetSliderPosition = MathCore_1.Clamp(0, max, offsetSliderPosition);
        }
        domElements.Slider.style.transform = "translate3d(" + -offsetSliderPosition + "px,0,0)";
    };
    var updateProgressBar = function () {
        var domElements = getDomElements();
        var unfinishedStepLength = 0;
        for (var i = 0; i < getUnfinishedStepIndex() + 1; i++) {
            unfinishedStepLength += domElements.StepContainer.children[i].offsetWidth;
        }
        domElements.ProgressBar.style.width = unfinishedStepLength + "px";
    };
    var getStepLabelText = function () {
        return Localization_1.strings.formatString(Localization_1.strings.ProgressTracker_Label_CurrentStep, CurrentStep + 1, Steps.length);
    };
    var setDisplayStepIndexLimit = function (index) {
        setDisplayStepIndex(MathCore_1.Clamp(0, getLastDisplayIndex(), index));
    };
    var handleOnKeyDown = function (e) {
        var target = e.target;
        var index = displayStepIndex;
        if (keyboard.IsSpace(e) || keyboard.IsEnter(e)) {
            target.click();
        }
        else if (keyboard.IsRightArrow(e)) {
            var nextTarget = target.nextSibling;
            if (nextTarget) {
                nextTarget.focus();
                index++;
            }
        }
        else if (keyboard.IsLeftArrow(e)) {
            var prevTarget = target.previousSibling;
            if (prevTarget) {
                prevTarget.focus();
                index--;
            }
        }
        setDisplayStepIndexLimit(index);
    };
    var handleStepOnClick = function (index, step) {
        if (OnClickStep) {
            OnClickStep(index, step);
        }
        setDisplayStepIndexLimit(index);
    };
    var handlePreviousOnClick = function () {
        setDisplayStepIndexLimit(displayStepIndex - 1);
    };
    var handleNextOnClick = function () {
        setDisplayStepIndexLimit(displayStepIndex + 1);
    };
    return (React.createElement(ProgressTracker_styles_1.ProgressTrackerContainerDiv, null,
        React.createElement(ProgressTracker_styles_1.ProgressTrackerInnerDiv, { ShowNav: showScroll },
            React.createElement(ScrollNavButton_1.ScrollNavButton, { Disabled: prevButtonDisabled, Show: showScroll, Type: "Previous", OnClick: handlePreviousOnClick }),
            React.createElement(ProgressTracker_styles_1.SliderContainerDiv, { ShowNav: showScroll, Margin: margin, ref: sliderContainerRef },
                React.createElement(ProgressTracker_styles_1.SliderDiv, { ref: sliderRef, onKeyDown: function (e) { return handleOnKeyDown(e); }, ShowNav: showScroll },
                    React.createElement(ProgressTracker_styles_1.ProgressBarContainerDiv, null,
                        React.createElement(ProgressTracker_styles_1.ProgressBarDiv, { ref: progressBarRef })),
                    React.createElement(ProgressTracker_styles_1.StepContainerDiv, { ref: stepContainerRef, role: "tablist" }, Steps.map(function (step, index) { return (React.createElement(Step_1.Step, { IsCurrent: CurrentStep === index, key: "progress-tracker-step-" + index, ClientId: step.ClientId, Title: step.Title, AriaLabel: step.AriaLabel, AriaControlsPanel: step.AriaControlsPanel, Disabled: step.Disabled, OptionalText: step.OptionalText, State: step.State, IsLink: step.IsLink, OnClick: function () { return handleStepOnClick(index, step); } })); })))),
            React.createElement(ScrollNavButton_1.ScrollNavButton, { Disabled: nextButtonDisabled, Show: showScroll, Type: "Next", OnClick: handleNextOnClick })),
        React.createElement(ProgressTracker_styles_1.StepLabelContainerDiv, null,
            React.createElement(ProgressTracker_styles_1.StepNumberSpan, null, getStepLabelText()))));
};
exports.ProgressTracker = ProgressTracker;
